/**
 * DeveloperTabs · v2.7.0 batch DM
 * ================================
 * Four sub-components mounted by the Administration tab when one of the
 * new Developer sub-routes is active:
 *   • DeveloperPortalTab   — paginated grid of external resource cards
 *   • DeveloperCodingTab   — Coming Soon banner
 *   • DeveloperBrandingTab — CRUD for the portal links
 *   • ApiKeysTab           — auto-generate + masked list + revoke
 *
 * Backend endpoints (all admin-auth):
 *   POST   /api/admin/api-keys
 *   GET    /api/admin/api-keys
 *   DELETE /api/admin/api-keys/{id}
 *   POST   /api/admin/developer-portal-links
 *   GET    /api/admin/developer-portal-links
 *   PUT    /api/admin/developer-portal-links/{id}
 *   DELETE /api/admin/developer-portal-links/{id}
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Loader2, Plus, Trash2, ExternalLink, Copy, Check, Globe, Code, KeyRound, Image as ImageIcon,
  ChevronLeft, ChevronRight, Pencil, Save, RefreshCw, X,
  Volume2, VolumeX, Bell, Siren, MessageCircle, Phone, Car, Flag, Square as SquareIcon,
  Upload, RotateCcw, FileAudio, Smartphone, AlarmClock,
} from "lucide-react";
import { toast } from "sonner";
import adminApi from "@/lib/adminApi";
import * as audioManager from "@/lib/audioManager";
import { broadcastSoundChange, subscribeSoundChange } from "@/lib/soundSync";


// ============================================================================
// Developer Portal — read-only paginated grid
// ============================================================================
const PORTAL_PAGE_SIZE = 12;

export function DeveloperPortalTab() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const load = useCallback(() => {
    setLoading(true);
    adminApi.get("/admin/developer-portal-links/public")
      .then((r) => setLinks(r.data?.links || []))
      .catch(() => toast.error("Could not load portal links"))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const totalPages = Math.max(1, Math.ceil(links.length / PORTAL_PAGE_SIZE));
  const pageLinks = useMemo(() => {
    const start = (page - 1) * PORTAL_PAGE_SIZE;
    return links.slice(start, start + PORTAL_PAGE_SIZE);
  }, [links, page]);

  if (loading) {
    return (
      <Card className="bg-zinc-900 border-zinc-800 p-8 text-center text-zinc-500" data-testid="dev-portal-loading">
        <Loader2 className="h-6 w-6 animate-spin inline mr-2"/>Loading portal links…
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="developer-portal-tab">
      <Card className="bg-zinc-900 border-zinc-800 p-5">
        <div className="flex items-start gap-3 flex-wrap mb-4">
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-bold uppercase tracking-widest text-amber-300 flex items-center gap-2">
              <Globe className="h-4 w-4"/>Developer Portal
            </h3>
            <p className="text-xs text-zinc-400 mt-1">
              Quick links to external developer dashboards (Square, Twilio, Resend, Sentry, etc.). Manage these links in <span className="text-amber-300 font-bold">Developer → Developer Branding</span>.
            </p>
          </div>
          <Button onClick={load} variant="outline" className="border-zinc-700 text-zinc-300 hover:bg-zinc-800" data-testid="dev-portal-refresh">
            <RefreshCw className="h-4 w-4 mr-1.5"/>Refresh
          </Button>
        </div>

        {links.length === 0 ? (
          <div className="text-center py-10 text-zinc-500" data-testid="dev-portal-empty">
            No portal links yet. Add one from <span className="text-amber-300 font-bold">Developer Branding</span>.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="dev-portal-grid">
              {pageLinks.map((l) => (
                <PortalCard key={l.id} link={l}/>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-5" data-testid="dev-portal-pagination">
                <Button variant="ghost" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} data-testid="dev-portal-prev">
                  <ChevronLeft className="h-4 w-4"/>
                </Button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <Button
                    key={p} size="sm"
                    variant={p === page ? "default" : "ghost"}
                    onClick={() => setPage(p)}
                    className={p === page ? "bg-amber-400 text-zinc-950 hover:bg-amber-300 font-bold" : "text-zinc-400"}
                    data-testid={`dev-portal-page-${p}`}
                  >{p}</Button>
                ))}
                <Button variant="ghost" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} data-testid="dev-portal-next">
                  <ChevronRight className="h-4 w-4"/>
                </Button>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}

function PortalCard({ link }) {
  const imgRef = useRef(null);
  return (
    <div className="bg-zinc-950 border border-zinc-800 hover:border-amber-400/40 rounded-lg p-3 flex flex-col items-center text-center transition-colors" data-testid={`dev-portal-card-${link.id}`}>
      <div className="w-[200px] h-[100px] flex items-center justify-center bg-zinc-900 rounded mb-3 overflow-hidden">
        {link.image_url ? (
          <img
            ref={imgRef}
            src={link.image_url}
            alt={link.title}
            className="max-w-[200px] max-h-[100px] object-contain"
            onError={() => { if (imgRef.current) imgRef.current.style.display = "none"; }}
          />
        ) : (
          <ImageIcon className="h-8 w-8 text-zinc-700"/>
        )}
      </div>
      <h4 className="text-sm font-bold text-zinc-100 mb-2 truncate w-full">{link.title}</h4>
      <a
        href={link.url}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-amber-400 hover:bg-amber-300 text-zinc-950 text-xs font-extrabold uppercase tracking-wider transition-colors"
        data-testid={`dev-portal-visit-${link.id}`}
      >
        Visit Site <ExternalLink className="h-3 w-3"/>
      </a>
    </div>
  );
}


// ============================================================================
// Developer Coding — Coming Soon
// ============================================================================
export function DeveloperCodingTab() {
  return (
    <Card className="bg-zinc-900 border-zinc-800 p-12" data-testid="developer-coding-tab">
      <div className="flex flex-col items-center text-center max-w-md mx-auto">
        <div className="relative mb-5">
          <div className="absolute inset-0 bg-amber-400/20 blur-2xl rounded-full"/>
          <div className="relative h-20 w-20 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 text-zinc-950 flex items-center justify-center shadow-2xl shadow-amber-500/40">
            <Code className="h-10 w-10"/>
          </div>
        </div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300 font-bold mb-2">Coming Soon</div>
        <h2 className="text-2xl font-extrabold text-zinc-100 mb-3">Developer Coding</h2>
        <p className="text-sm text-zinc-400 leading-relaxed">
          A unified in-app code editor and snippet vault is on the way. You&apos;ll be able to maintain custom CSS overrides, JavaScript hooks, and webhook templates without leaving the admin console.
        </p>
        <div className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-zinc-950 border border-zinc-800 text-[10px] uppercase tracking-widest text-zinc-500">
          <span className="h-2 w-2 rounded-full bg-amber-400 animate-pulse"/>
          Targeted for v2.8 release
        </div>
      </div>
    </Card>
  );
}


// ============================================================================
// Developer Branding — CRUD for portal links
// ============================================================================
export function DeveloperBrandingTab() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // null | {} (creating) | {id, …} (editing)
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    adminApi.get("/admin/developer-portal-links")
      .then((r) => setLinks(r.data?.links || []))
      .catch(() => toast.error("Could not load portal links"))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const startCreate = () => setEditing({ title: "", url: "", image_url: "", sort_order: 0 });
  const startEdit = (l) => setEditing({ ...l });
  const cancel = () => setEditing(null);

  const save = async () => {
    if (!editing) return;
    if (!editing.title?.trim() || !editing.url?.trim()) {
      toast.error("Title and URL are required.");
      return;
    }
    setSaving(true);
    try {
      if (editing.id) {
        await adminApi.put(`/admin/developer-portal-links/${editing.id}`, {
          title: editing.title.trim(),
          url: editing.url.trim(),
          image_url: editing.image_url?.trim() || null,
          sort_order: Number(editing.sort_order) || 0,
        });
        toast.success("Link updated");
      } else {
        await adminApi.post("/admin/developer-portal-links", {
          title: editing.title.trim(),
          url: editing.url.trim(),
          image_url: editing.image_url?.trim() || null,
          sort_order: Number(editing.sort_order) || 0,
        });
        toast.success("Link created");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const removeLink = async (id) => {
    if (!window.confirm("Delete this portal link?")) return;
    try {
      await adminApi.delete(`/admin/developer-portal-links/${id}`);
      toast.success("Link deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image must be ≤ 2 MB. Compress and try again.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      // Store as a data: URL so the image survives without needing a CDN —
      // images render directly from the database row. For volumes >500 KB
      // per image, consider upgrading to S3/cloud storage later.
      setEditing((cur) => cur ? { ...cur, image_url: reader.result } : cur);
    };
    reader.readAsDataURL(file);
  };

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-5 space-y-4" data-testid="developer-branding-tab">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold uppercase tracking-widest text-amber-300 flex items-center gap-2">
            <Pencil className="h-4 w-4"/>Developer Branding · Portal Links
          </h3>
          <p className="text-xs text-zinc-400 mt-1">
            Add, edit, and delete the external links that show up in <span className="text-amber-300 font-bold">Developer → Developer Portal</span>. Each card displays the title, an image (max 200×100), and a "Visit Site" button.
          </p>
        </div>
        {!editing && (
          <Button onClick={startCreate} className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-extrabold" data-testid="dev-branding-add">
            <Plus className="h-4 w-4 mr-1.5"/>Add Link
          </Button>
        )}
      </div>

      {editing && (
        <Card className="bg-zinc-950 border-amber-400/40 p-4 space-y-3" data-testid="dev-branding-form">
          <h4 className="text-xs uppercase tracking-widest text-amber-300 font-bold">
            {editing.id ? "Edit Link" : "New Portal Link"}
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Title</Label>
              <Input
                value={editing.title || ""}
                onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                placeholder="e.g. Square Dashboard"
                className="bg-zinc-900 border-zinc-700 mt-1"
                data-testid="dev-branding-title"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Target URL</Label>
              <Input
                value={editing.url || ""}
                onChange={(e) => setEditing({ ...editing, url: e.target.value })}
                placeholder="https://…"
                className="bg-zinc-900 border-zinc-700 mt-1"
                data-testid="dev-branding-url"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Sort Order</Label>
              <Input
                type="number"
                value={editing.sort_order ?? 0}
                onChange={(e) => setEditing({ ...editing, sort_order: e.target.value })}
                className="bg-zinc-900 border-zinc-700 mt-1"
                data-testid="dev-branding-sort"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Logo / Image (≤ 2 MB)</Label>
              <Input
                type="file"
                accept="image/*"
                onChange={uploadImage}
                className="bg-zinc-900 border-zinc-700 mt-1 file:bg-amber-400/20 file:border-0 file:text-amber-300 file:font-bold file:px-2 file:py-1 file:rounded file:mr-2"
                data-testid="dev-branding-image-upload"
              />
            </div>
          </div>
          {editing.image_url && (
            <div className="bg-zinc-900 rounded p-3 flex items-center gap-3" data-testid="dev-branding-preview">
              <img src={editing.image_url} alt="preview" className="max-w-[200px] max-h-[100px] object-contain bg-zinc-950 rounded"/>
              <div className="flex-1 text-xs text-zinc-400">Preview · this is what the Portal page will render.</div>
              <Button variant="ghost" onClick={() => setEditing({ ...editing, image_url: "" })} className="text-red-300 hover:bg-red-900/20" data-testid="dev-branding-clear-image">
                <X className="h-4 w-4"/>
              </Button>
            </div>
          )}
          <div className="flex gap-2 pt-1">
            <Button onClick={save} disabled={saving} className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold" data-testid="dev-branding-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin"/> : <><Save className="h-4 w-4 mr-1.5"/>Save</>}
            </Button>
            <Button variant="ghost" onClick={cancel} className="text-zinc-300 hover:bg-zinc-800" data-testid="dev-branding-cancel">
              Cancel
            </Button>
          </div>
        </Card>
      )}

      {loading ? (
        <div className="text-center py-8 text-zinc-500"><Loader2 className="h-5 w-5 animate-spin inline"/></div>
      ) : links.length === 0 ? (
        <div className="text-center py-10 text-zinc-500" data-testid="dev-branding-empty">No portal links yet. Click "Add Link" above.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="dev-branding-list">
          {links.map((l) => (
            <div key={l.id} className="bg-zinc-950 border border-zinc-800 rounded p-3 flex items-center gap-3" data-testid={`dev-branding-row-${l.id}`}>
              <div className="w-16 h-10 bg-zinc-900 rounded overflow-hidden flex items-center justify-center flex-shrink-0">
                {l.image_url ? <img src={l.image_url} alt="" className="max-w-full max-h-full object-contain"/> : <ImageIcon className="h-4 w-4 text-zinc-700"/>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-zinc-100 truncate">{l.title}</div>
                <div className="text-[10px] text-zinc-500 truncate">{l.url}</div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => startEdit(l)} className="text-amber-300" data-testid={`dev-branding-edit-${l.id}`}>
                <Pencil className="h-3.5 w-3.5"/>
              </Button>
              <Button variant="ghost" size="sm" onClick={() => removeLink(l.id)} className="text-red-300" data-testid={`dev-branding-delete-${l.id}`}>
                <Trash2 className="h-3.5 w-3.5"/>
              </Button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}


// ============================================================================
// API Keys — auto-generate + masked list + revoke
// ============================================================================
export function ApiKeysTab() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [label, setLabel] = useState("");
  const [scopes, setScopes] = useState({ read: true, quote: true });
  const [revealedKey, setRevealedKey] = useState(null);  // string · only shown once after create
  const [copied, setCopied] = useState(false);
  const [includeRevoked, setIncludeRevoked] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    adminApi.get(`/admin/api-keys?include_revoked=${includeRevoked ? "true" : "false"}`)
      .then((r) => setKeys(r.data?.keys || []))
      .catch(() => toast.error("Could not load API keys"))
      .finally(() => setLoading(false));
  }, [includeRevoked]);
  useEffect(() => { load(); }, [load]);

  const createKey = async () => {
    if (!label.trim()) {
      toast.error("Give the key a name so you can identify it later.");
      return;
    }
    setCreating(true);
    setRevealedKey(null);
    try {
      const scopeList = Object.entries(scopes).filter(([_, v]) => v).map(([k]) => k);
      const { data } = await adminApi.post("/admin/api-keys", { label: label.trim(), scopes: scopeList });
      setRevealedKey(data.full_key);
      setLabel("");
      toast.success("API key generated · copy it now");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Key generation failed");
    } finally { setCreating(false); }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this key? Any apps using it will start receiving 401 immediately.")) return;
    try {
      await adminApi.delete(`/admin/api-keys/${id}`);
      toast.success("Key revoked");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Revoke failed");
    }
  };

  const copy = async () => {
    if (!revealedKey) return;
    try {
      await navigator.clipboard.writeText(revealedKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Clipboard blocked · highlight + copy manually");
    }
  };

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-5 space-y-4" data-testid="api-keys-tab">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold uppercase tracking-widest text-amber-300 flex items-center gap-2">
            <KeyRound className="h-4 w-4"/>API Keys
          </h3>
          <p className="text-xs text-zinc-400 mt-1">
            Generate keys for customer apps that consume your <span className="text-amber-300 font-mono">/api/v1/*</span> endpoints. Keys are SHA-256 hashed at rest — once you leave this screen the full value can never be retrieved again.
          </p>
        </div>
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-zinc-500">
          <Switch checked={includeRevoked} onCheckedChange={setIncludeRevoked} id="show-revoked"/>
          <label htmlFor="show-revoked" className="cursor-pointer">Show revoked</label>
        </div>
      </div>

      <Card className="bg-zinc-950 border-amber-400/30 p-4 space-y-3" data-testid="api-keys-create-card">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
          <div className="sm:col-span-2">
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Key Name / Client</Label>
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Mobile App · Production"
              className="bg-zinc-900 border-zinc-700 mt-1"
              data-testid="api-keys-label-input"
            />
          </div>
          <Button onClick={createKey} disabled={creating} className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-extrabold" data-testid="api-keys-generate">
            {creating ? <Loader2 className="h-4 w-4 animate-spin"/> : <><Plus className="h-4 w-4 mr-1.5"/>Auto-Generate</>}
          </Button>
        </div>
        <div className="flex items-center gap-3 text-xs text-zinc-400">
          <span className="uppercase tracking-wider font-bold text-zinc-500">Scopes:</span>
          {Object.keys(scopes).map((s) => (
            <label key={s} className="flex items-center gap-1.5 cursor-pointer">
              <Switch checked={scopes[s]} onCheckedChange={(v) => setScopes({ ...scopes, [s]: v })} id={`scope-${s}`}/>
              <span>{s}</span>
            </label>
          ))}
        </div>
      </Card>

      {revealedKey && (
        <Card className="bg-amber-400/10 border-2 border-amber-400/60 p-4" data-testid="api-keys-revealed-card">
          <div className="text-[10px] uppercase tracking-widest text-amber-300 font-extrabold mb-2 flex items-center gap-1.5">
            <KeyRound className="h-3.5 w-3.5"/>Your new key · COPY NOW · we cannot show this again
          </div>
          <div className="flex items-center gap-2 bg-zinc-950 border border-amber-400/40 rounded p-2.5">
            <code className="flex-1 text-amber-200 text-sm font-mono break-all" data-testid="api-keys-revealed-value">{revealedKey}</code>
            <Button onClick={copy} variant="outline" className="border-amber-400/60 text-amber-300 hover:bg-amber-400/20 flex-shrink-0" data-testid="api-keys-copy">
              {copied ? <><Check className="h-3.5 w-3.5 mr-1"/>Copied</> : <><Copy className="h-3.5 w-3.5 mr-1"/>Copy</>}
            </Button>
          </div>
        </Card>
      )}

      <div className="border-t border-zinc-800 pt-3">
        <h4 className="text-xs uppercase tracking-widest text-zinc-400 font-bold mb-2">Current Active Keys</h4>
        {loading ? (
          <div className="text-center py-6 text-zinc-500"><Loader2 className="h-5 w-5 animate-spin inline"/></div>
        ) : keys.length === 0 ? (
          <div className="text-center py-8 text-zinc-500" data-testid="api-keys-empty">
            No keys yet. Use the form above to generate your first one.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="api-keys-table">
              <thead className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-800">
                <tr>
                  <th className="text-left p-2">Key Name / Client</th>
                  <th className="text-left p-2">Masked Key</th>
                  <th className="text-left p-2">Created</th>
                  <th className="text-left p-2">Last Used</th>
                  <th className="text-right p-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {keys.map((k) => (
                  <tr key={k.id} className={`border-b border-zinc-900 ${k.revoked_at ? "opacity-50" : ""}`} data-testid={`api-keys-row-${k.id}`}>
                    <td className="p-2 text-zinc-100 font-bold">{k.label}</td>
                    <td className="p-2 text-zinc-300 font-mono text-xs">{k.masked}</td>
                    <td className="p-2 text-zinc-500 text-xs">{k.created_at?.slice(0, 10)}</td>
                    <td className="p-2 text-zinc-500 text-xs">{k.last_used_at?.slice(0, 16).replace("T", " ") || "—"}</td>
                    <td className="p-2 text-right">
                      {k.revoked_at ? (
                        <span className="text-[10px] uppercase tracking-wider text-red-300 font-bold">Revoked</span>
                      ) : (
                        <Button variant="ghost" size="sm" onClick={() => revoke(k.id)} className="text-red-300 hover:bg-red-900/20" data-testid={`api-keys-revoke-${k.id}`}>
                          <Trash2 className="h-3.5 w-3.5 mr-1"/>Revoke
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Card>
  );
}


// ============================================================================
// Role-Assignment PINs · v2.7.0 batch DO+
// Per-role PIN configuration. When a role has a PIN set, anyone other than
// Operating Partner / Developer must enter THAT exact PIN to grant the role.
// Powered by GET/PUT /api/admin/role-pins.
// ============================================================================
export function RolePinsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [drafts, setDrafts] = useState({});

  const reload = useCallback(() => {
    setLoading(true);
    adminApi.get("/admin/role-pins")
      .then((r) => setRows(r.data?.roles || []))
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not load PIN config"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const setPin = async (role) => {
    const pin = (drafts[role] || "").trim();
    if (pin && (!/^\d{4,8}$/.test(pin))) {
      toast.error("PIN must be 4–8 digits");
      return;
    }
    setBusy((b) => ({ ...b, [role]: true }));
    try {
      await adminApi.put("/admin/role-pins", { role, pin });
      toast.success(pin ? `${role.replace(/_/g, " ")} PIN updated` : `${role.replace(/_/g, " ")} PIN cleared`);
      setDrafts((d) => ({ ...d, [role]: "" }));
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy((b) => ({ ...b, [role]: false }));
    }
  };

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="role-pins-tab">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-bold flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-emerald-300"/>
            Role-Assignment PINs
          </h3>
          <p className="text-[11px] text-zinc-400 mt-0.5 max-w-2xl">
            Configure per-role PINs that lower-tier callers (Dispatchers) must
            enter to grant a specific role. When unconfigured, the system falls
            back to any Operating Partner / Developer PIN. Operating Partner &
            Developer themselves never need to enter a PIN.
          </p>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={reload}
          className="text-zinc-400 hover:text-zinc-200"
          data-testid="role-pins-refresh"
        >
          <RefreshCw className="h-3 w-3 mr-1"/>Refresh
        </Button>
      </div>
      {loading ? (
        <div className="py-10 text-center text-zinc-400 text-sm">
          <Loader2 className="h-4 w-4 inline mr-2 animate-spin"/>Loading…
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <div
              key={r.role}
              data-testid={`role-pin-row-${r.role}`}
              className="flex items-center gap-3 rounded border border-zinc-800 bg-zinc-950 p-3"
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold capitalize">{r.role.replace(/_/g, " ")}</div>
                <div className="text-[10px] text-zinc-500 mt-0.5">
                  {r.configured ? (
                    <>Configured · <span className="font-mono text-amber-300" data-testid={`role-pin-mask-${r.role}`}>{r.masked}</span></>
                  ) : (
                    <span className="text-zinc-600">No PIN — falls back to OP/Dev driver PIN</span>
                  )}
                </div>
              </div>
              <Input
                value={drafts[r.role] || ""}
                onChange={(e) => setDrafts((d) => ({ ...d, [r.role]: e.target.value.replace(/\D/g, "").slice(0, 8) }))}
                placeholder="4–8 digits"
                inputMode="numeric"
                maxLength={8}
                className="bg-zinc-900 border-zinc-700 text-center tracking-[0.3em] font-mono text-sm w-32"
                data-testid={`role-pin-input-${r.role}`}
              />
              <Button
                size="sm"
                onClick={() => setPin(r.role)}
                disabled={busy[r.role]}
                className="bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-bold text-xs"
                data-testid={`role-pin-save-${r.role}`}
              >
                {busy[r.role] ? <Loader2 className="h-3 w-3 mr-1 animate-spin"/> : <Save className="h-3 w-3 mr-1"/>}
                {(drafts[r.role] || "").length ? "Set" : (r.configured ? "Clear" : "Set")}
              </Button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}


// ============================================================================
// Audio Test Panel · v2.7.7
// ============================================================================
// Developer-only diagnostic surface that fires every operational audio event
// on demand so dispatch can tune chime volume + verify routing without
// having to wait for a real SOS / chat / booking / dispatch.
//
// The panel dispatches the same `rtg:*` CustomEvents that the live pipeline
// fires, so `useAudioAlerts` (already mounted at the admin dashboard root)
// handles playback transparently. We respect the per-user mute state via
// localStorage just like the live events do.
// ============================================================================
const AUDIO_EVENTS = [
  { key: "panic",          event: "rtg:panic_alert",        file: "/sounds/panic-sos-emergency-alert.mp3", Icon: Siren,   label: "Panic / SOS",         desc: "Persistent SOS loop until acknowledged",   accent: "red"    },
  { key: "panic_ack",      event: "rtg:panic_acknowledged", file: null,                            Icon: VolumeX,      label: "Stop SOS Loop",       desc: "Sends acknowledgement → stops SOS",        accent: "emerald"},
  { key: "booking",        event: "rtg:public_booking",     file: "/sounds/online-booking-init.mp3", Icon: Phone,     label: "Dispatch / Call-In Booking", desc: "New dispatch, Book Now, or Katie AI booking (looping alarm)", accent: "amber"  },
  // v2.8+E285 · Distinct RIDER-APP booking klaxon. Fires the SAME
  // rtg:public_booking event but tags source=rider_account so the engine
  // layers its own tone. Uploadable independently of the dispatch tone.
  { key: "booking_rider",  event: "rtg:public_booking",     file: "/sounds/online-booking-alert.mp3", Icon: Smartphone, label: "Rider App Booking", desc: "New booking placed from the Rider App (source=rider_account) — layers its own looping alarm over the dispatch tone", accent: "emerald", fireDetail: { source: "rider_account" } },
  // v2.8+E295 · Distinct PRIORITY/URGENT booking alarm. Fired by the Ride
  // Coordinator board when a booking is flagged urgent OR an unassigned ride
  // auto-escalates past 5 min. Layers over the standard booking tones and
  // follows the Rider-App volume slider. Upload a louder custom priority tone.
  { key: "booking_urgent", event: "rtg:booking_urgent",    file: "/sounds/online-booking-init.mp3", Icon: AlarmClock, label: "Priority / Urgent Booking", desc: "Fires when a booking is flagged URGENT or an unassigned ride auto-escalates past 5 min — a distinct looping alarm that layers over the standard booking tone (follows the Rider-App volume). Upload your own louder priority tone here.", accent: "red" },
  { key: "driver_online",  event: "rtg:driver_online",      file: "/sounds/driver-online-alert.mp3", Icon: Car,        label: "Driver Online",       desc: "Driver transitioned offline→online",       accent: "sky"    },
  { key: "chat",           event: "rtg:chat_message",       file: "/sounds/chat-message-alert.mp3", Icon: MessageCircle,label: "Chat Message",        desc: "New chatbot message arrived",              accent: "indigo" },
  { key: "chat_direct",    event: "rtg:chat_message_direct", file: "/sounds/chat-message-alert.mp3", Icon: MessageCircle,label: "Chat Message (Direct)", desc: "A live-chat customer message hit MY session (Katie escalation, DM, or agent transfer)", accent: "sky", noUpload: true, sharesKey: "chat" },
  { key: "trip_initiated", event: "rtg:trip_initiated",     file: "/sounds/trip-initiated-alert.mp3", Icon: Flag,       label: "Trip Initiated",      desc: "Driver opened a new meter trip",           accent: "amber"  },
  // v2.7.65+E221 · DRIVER-app incoming dispatch alert (plays on the driver's
  // device, not in the console). Preview + upload only — no console "Live"
  // event since it fires on the driver's phone, not here.
  { key: "driver_dispatch", event: null,                     file: "/sounds/driver-dispatch-alert.mp3", Icon: Car,      label: "Driver Dispatch Alert", desc: "Plays on the DRIVER's device when dispatch pushes a ride (Book Now / Ride Coordinator). Delivered via push so it alerts even in the background.", accent: "emerald", noLive: true },
];

const ACCENT_BORDERS = {
  red:     "border-red-400/40 hover:border-red-400 hover:bg-red-400/10 text-red-300",
  emerald: "border-emerald-400/40 hover:border-emerald-400 hover:bg-emerald-400/10 text-emerald-300",
  amber:   "border-amber-400/40 hover:border-amber-400 hover:bg-amber-400/10 text-amber-300",
  sky:     "border-sky-400/40 hover:border-sky-400 hover:bg-sky-400/10 text-sky-300",
  indigo:  "border-indigo-400/40 hover:border-indigo-400 hover:bg-indigo-400/10 text-indigo-300",
};

export function AudioTestPanelTab() {
  const [volume, setVolume] = useState(85);            // 0-100
  const [muted, setMuted] = useState(
    typeof window !== "undefined" && window.localStorage?.getItem("rtg_admin_audio_muted") === "1"
  );
  const [lastFired, setLastFired] = useState(null);
  const liveAudios = useRef(new Set());                // direct-play handles (for Stop All)

  // v2.7.45+E17 · Custom-sound overrides loaded from /api/admin/system-sounds.
  // Shape: { [key]: { custom_url, custom_filename, size_bytes, uploaded_at, uploaded_by } | null }
  const [customSounds, setCustomSounds] = useState({});
  const [uploadingKey, setUploadingKey] = useState(null);
  const [savingConfig, setSavingConfig] = useState(false);
  const [maxBytes, setMaxBytes] = useState(100 * 1024 * 1024);

  const reloadCustomSounds = useCallback(async () => {
    try {
      const { data } = await adminApi.get("/admin/system-sounds");
      const map = {};
      (data?.items || []).forEach((r) => { map[r.key] = r.custom_url ? r : null; });
      setCustomSounds(map);
      if (data?.max_bytes) setMaxBytes(data.max_bytes);
    } catch (e) {
      // 403 = current user isn't a developer/admin/partner. That's fine —
      // they just don't get the upload controls.
      if (e?.response?.status !== 403) {
        toast.error(`Could not load custom sounds: ${e?.response?.data?.detail || e.message}`);
      }
    }
  }, []);

  useEffect(() => { reloadCustomSounds(); }, [reloadCustomSounds]);

  // v2.7.65+E226 · Refresh the CUSTOM badges instantly when a sound changes in
  // any other open console tab (BroadcastChannel) or this one.
  useEffect(() => subscribeSoundChange(() => reloadCustomSounds()), [reloadCustomSounds]);

  // Keep mute state synced when the admin toggles it in the profile modal.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onChange = (e) => setMuted(!!e?.detail?.muted);
    window.addEventListener("rtg:audio_mute_changed", onChange);
    return () => window.removeEventListener("rtg:audio_mute_changed", onChange);
  }, []);

  // Fire the SAME custom event the live pipeline dispatches. The
  // useAudioAlerts hook listens for these and plays the right sound at
  // its own internal volume (0.85 single-shot / 1.0 SOS loop).
  const fireEvent = useCallback((evt) => {
    try {
      window.dispatchEvent(new CustomEvent(evt.event, { detail: { id: `audio-test-${Date.now()}`, source: "audio_test_panel", ...(evt.fireDetail || {}) }}));
      setLastFired({ key: evt.key, label: evt.label, at: new Date().toISOString() });
      toast.success(`Fired: ${evt.label}`);
    } catch (err) {
      toast.error(`Could not fire ${evt.label}`);
      void err;
    }
  }, []);

  // Direct-play bypasses the engine so the slider's `volume` value applies.
  // Useful when tuning a specific chime's loudness on the host machine.
  // v2.7.45+E17 — uses the custom-uploaded URL when present.
  const directPlay = useCallback((evt) => {
    // v2.7.65+E220 · Mirror the live engine: a section that shares another
    // key's tone (Direct Chat → Chat) previews that shared custom upload.
    const srcKey = evt.sharesKey || evt.key;
    const file = customSounds[srcKey]?.custom_url || customSounds[evt.key]?.custom_url || evt.file;
    if (!file) return;
    if (muted) { toast.warning("Audio is muted — unmute via the profile modal to hear test chimes"); return; }
    try {
      const a = new Audio(file);
      a.volume = Math.max(0, Math.min(1, volume / 100));
      liveAudios.current.add(a);
      a.addEventListener("ended", () => liveAudios.current.delete(a));
      const p = a.play();
      // v2.7.65+E219 · Governed: test-panel direct-play is capped at 30s.
      audioManager.trackWhenPlaying(a, p, { kind: "standard" });
      if (p && typeof p.catch === "function") p.catch(() => toast.warning("Browser blocked autoplay — click somewhere first"));
      setLastFired({ key: `${evt.key}-direct`, label: `${evt.label} (direct${customSounds[srcKey]?.custom_url ? " · custom" : ""})`, at: new Date().toISOString() });
    } catch (err) {
      toast.error(`Direct-play failed: ${evt.label}`);
      void err;
    }
  }, [volume, muted, customSounds]);

  // v2.7.45+E17 · Upload a custom audio file for a specific key. The
  // backend enforces the same 100 MB cap + audio-MIME guardrail so we
  // can render an instant client-side warning AND still rely on the
  // server response for the final say.
  const uploadCustom = useCallback(async (evt, fileList) => {
    const file = fileList && fileList[0];
    if (!file) return;
    // Front-end pre-check — cheap UX, real check happens server-side.
    const validExts = [".mp3", ".wav", ".ogg", ".oga", ".m4a", ".aac", ".flac", ".webm"];
    const lower = (file.name || "").toLowerCase();
    const matches = validExts.some((ext) => lower.endsWith(ext)) || (file.type || "").startsWith("audio/");
    if (!matches) {
      toast.error(`Only audio files are accepted (.mp3, .wav, .ogg, .m4a, .aac, .flac). Got "${file.type || "?"}".`);
      return;
    }
    if (file.size > maxBytes) {
      toast.error(`File too large: ${(file.size / 1024 / 1024).toFixed(1)} MB exceeds the 100 MB cap.`);
      return;
    }
    setUploadingKey(evt.key);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await adminApi.post(`/admin/system-sounds/${evt.key}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Custom audio set for ${evt.label} · ${(data.size_bytes / 1024 / 1024).toFixed(2)} MB`);
      await reloadCustomSounds();
      // v2.8 · Tell the live audio engine to pick up the new tone immediately.
      // v2.7.65+E226 · Broadcast to THIS tab + all other open consoles at once.
      broadcastSoundChange({ key: evt.key });
    } catch (err) {
      toast.error(`Upload failed: ${err?.response?.data?.detail || err.message}`);
    } finally { setUploadingKey(null); }
  }, [maxBytes, reloadCustomSounds]);

  const resetCustom = useCallback(async (evt) => {
    if (!window.confirm(`Reset "${evt.label}" back to the factory default chime?`)) return;
    try {
      await adminApi.delete(`/admin/system-sounds/${evt.key}`);
      // v2.7.45+E17 fix · invalidate the row's custom state immediately so
      // the CUSTOM badge disappears without waiting for a full reload.
      setCustomSounds((prev) => ({ ...prev, [evt.key]: null }));
      toast.success(`Reset "${evt.label}" to default`);
      // Re-fetch in the background to make sure server-side truth wins.
      reloadCustomSounds();
      // v2.8 · Tell the live audio engine to revert to the default tone now.
      // v2.7.65+E226 · Broadcast to THIS tab + all other open consoles at once.
      broadcastSoundChange({ key: evt.key });
    } catch (err) {
      toast.error(`Reset failed: ${err?.response?.data?.detail || err.message}`);
    }
  }, [reloadCustomSounds]);

  const saveAudioConfig = useCallback(async () => {
    setSavingConfig(true);
    try {
      await adminApi.post("/admin/system-sounds/save", { note: "" });
      toast.success("Audio configuration saved");
    } catch (err) {
      toast.error(`Save failed: ${err?.response?.data?.detail || err.message}`);
    } finally { setSavingConfig(false); }
  }, []);

  const stopAll = useCallback(() => {
    // 1. Stop the SOS loop via the same hook event.
    try { window.dispatchEvent(new CustomEvent("rtg:panic_acknowledged", { detail: {}})); } catch { /* noop */ }
    // v2.7.65+E219 · Also stop every governed audio element (booking klaxon,
    // chimes, Ashley TTS, panic/SOS) via the central master stop.
    try { audioManager.stopAll(); } catch { /* noop */ }
    try { window.dispatchEvent(new CustomEvent("rtg:audio_master_stopped")); } catch { /* noop */ }
    // 2. Stop any direct-play handles.
    liveAudios.current.forEach((a) => { try { a.pause(); a.currentTime = 0; } catch { /* noop */ } });
    liveAudios.current.clear();
    toast.info("All audio stopped");
  }, []);

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-5 space-y-4" data-testid="audio-test-panel-tab">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold uppercase tracking-widest text-amber-300 flex items-center gap-2">
            <Volume2 className="h-4 w-4"/>Audio Test Panel
          </h3>
          <p className="text-xs text-zinc-400 mt-1">
            Manually fire every operational audio chime to verify routing, tune volume, and confirm the dispatcher will hear what they need to hear. <span className="text-amber-300 font-bold">Live</span> buttons dispatch the same DOM event the production pipeline fires. <span className="text-amber-300 font-bold">Direct</span> buttons play the asset locally honoring the volume slider below.
          </p>
        </div>
        <Button
          onClick={stopAll}
          variant="outline"
          className="bg-zinc-950 border-red-400/40 text-red-300 hover:bg-red-400/10 hover:border-red-400 font-bold"
          data-testid="audio-test-stop-all"
        >
          <SquareIcon className="h-3.5 w-3.5 mr-1.5"/>STOP ALL
        </Button>
      </div>

      {/* Mute warning */}
      {muted && (
        <Card className="bg-red-500/10 border-red-400/40 p-3" data-testid="audio-test-muted-warning">
          <div className="flex items-center gap-2 text-red-200 text-xs font-bold">
            <VolumeX className="h-4 w-4 flex-shrink-0"/>
            Operational audio is currently MUTED for your account. "Live" events will fire but no sound plays. Unmute via your profile avatar → Profile Modal → Operational Audio.
          </div>
        </Card>
      )}

      {/* Volume slider */}
      <Card className="bg-zinc-950 border-zinc-800 p-4" data-testid="audio-test-volume-card">
        <div className="flex items-center gap-3">
          <Volume2 className="h-4 w-4 text-amber-300 flex-shrink-0"/>
          <Label className="text-xs uppercase tracking-wider text-zinc-400 font-bold w-32 flex-shrink-0">Direct Volume</Label>
          <input
            type="range"
            min={0}
            max={100}
            step={1}
            value={volume}
            onChange={(e) => setVolume(Number(e.target.value))}
            className="flex-1 accent-amber-400"
            data-testid="audio-test-volume-slider"
          />
          <div className="text-xs font-mono text-amber-300 w-12 text-right" data-testid="audio-test-volume-readout">{volume}%</div>
        </div>
        <p className="text-[10px] text-zinc-500 mt-2 ml-7">
          Slider only affects "Direct" playback. "Live" events go through the production engine's fixed levels (0.85 single-shot / 1.0 SOS loop).
        </p>
      </Card>

      {/* Event grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="audio-test-grid">
        {AUDIO_EVENTS.map((evt) => {
          const EvtIcon = evt.Icon;
          const cls = ACCENT_BORDERS[evt.accent] || ACCENT_BORDERS.amber;
          const custom = customSounds[evt.key];
          const isUploading = uploadingKey === evt.key;
          return (
            <Card key={evt.key} className="bg-zinc-950 border-zinc-800 p-3 space-y-2" data-testid={`audio-test-row-${evt.key}`}>
              <div className="flex items-start gap-2">
                <EvtIcon className={`h-5 w-5 flex-shrink-0 ${cls.split(" ").find((c) => c.startsWith("text-")) || "text-zinc-300"}`}/>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-zinc-100 truncate flex items-center gap-1.5">
                    {evt.label}
                    {custom?.custom_url && (
                      <span className="text-[9px] uppercase tracking-widest font-bold bg-emerald-500/20 text-emerald-300 px-1.5 py-0.5 rounded" data-testid={`audio-test-custom-badge-${evt.key}`}>CUSTOM</span>
                    )}
                  </div>
                  <div className="text-[10px] text-zinc-500 truncate">{evt.event}</div>
                  <div className="text-[10px] text-zinc-400 mt-0.5">{evt.desc}</div>
                  {(() => {
                    // v2.7.65+E226 · What dispatchers will ACTUALLY hear for
                    // this action (custom upload vs factory default). Mirror
                    // sections (e.g. Direct Chat) reflect their shared key.
                    const activeKey = evt.sharesKey || evt.key;
                    const isCustom = !!customSounds[activeKey]?.custom_url;
                    return (
                      <div
                        className="text-[10px] mt-1 flex items-center gap-1.5"
                        data-testid={`audio-test-active-${evt.key}`}
                        data-active-source={isCustom ? "custom" : "default"}
                      >
                        <span className={`h-1.5 w-1.5 rounded-full flex-shrink-0 ${isCustom ? "bg-emerald-400" : "bg-zinc-500"}`}/>
                        <span className="text-zinc-500">Active:</span>
                        {isCustom
                          ? <span className="text-emerald-300 font-bold">Custom ✓{evt.sharesKey ? ` (${evt.sharesKey})` : ""}</span>
                          : <span className="text-zinc-400 font-bold">Default</span>}
                      </div>
                    );
                  })()}
                  {custom?.custom_url && (
                    <div className="text-[10px] text-emerald-400/80 mt-1 truncate font-mono" data-testid={`audio-test-custom-info-${evt.key}`}>
                      <FileAudio className="h-2.5 w-2.5 inline mr-1"/>
                      {custom.custom_filename} · {(custom.size_bytes / 1024 / 1024).toFixed(2)} MB
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1.5 pt-1">
                {!evt.noLive && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => fireEvent(evt)}
                    className={`flex-1 bg-zinc-900 text-[11px] font-bold ${cls}`}
                    data-testid={`audio-test-fire-${evt.key}`}
                    title="Dispatch the WebSocket-style event"
                  >
                    <Bell className="h-3 w-3 mr-1"/>Live
                  </Button>
                )}
                {evt.file && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => directPlay(evt)}
                    className="flex-1 bg-zinc-900 border-zinc-700 hover:border-amber-400 hover:text-amber-300 text-[11px] font-bold"
                    data-testid={`audio-test-direct-${evt.key}`}
                    title="Play the file directly at slider volume"
                  >
                    <Volume2 className="h-3 w-3 mr-1"/>{evt.noLive ? "Preview" : "Direct"}
                  </Button>
                )}
              </div>
              {/* v2.7.45+E17 · Custom-audio upload row (developer/admin/partner only) */}
              {evt.file && !evt.noUpload && (
                <div className="flex items-center gap-1.5 border-t border-zinc-800/60 pt-2">
                  <label
                    className="flex-1 cursor-pointer text-[10px] uppercase tracking-widest font-bold text-zinc-400 hover:text-amber-300 transition-colors flex items-center gap-1.5 border border-zinc-800 hover:border-amber-400/60 rounded px-2 py-1.5 bg-zinc-900"
                    data-testid={`audio-test-upload-${evt.key}`}
                  >
                    {isUploading ? <Loader2 className="h-3 w-3 animate-spin"/> : <Upload className="h-3 w-3"/>}
                    {isUploading ? "Uploading…" : "Upload Custom Audio"}
                    <input
                      type="file"
                      accept=".mp3,.wav,.ogg,.oga,.m4a,.aac,.flac,.webm,audio/*"
                      className="sr-only"
                      onChange={(e) => uploadCustom(evt, e.target.files)}
                      disabled={isUploading}
                      data-testid={`audio-test-upload-input-${evt.key}`}
                    />
                  </label>
                  {custom?.custom_url && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => resetCustom(evt)}
                      className="bg-zinc-900 border-zinc-700 hover:border-red-400/60 hover:text-red-300 h-[30px] px-2"
                      title="Reset to factory-default chime"
                      data-testid={`audio-test-reset-${evt.key}`}
                    >
                      <RotateCcw className="h-3 w-3"/>
                    </Button>
                  )}
                </div>
              )}
              {/* v2.7.65+E220 · Sections that mirror another key (e.g. Direct Chat
                  shares the Chat Message tone) get no independent upload — the
                  engine derives their sound from the shared key. */}
              {evt.noUpload && evt.sharesKey && (
                <div className="border-t border-zinc-800/60 pt-2 text-[10px] text-zinc-500 flex items-center gap-1.5" data-testid={`audio-test-shares-${evt.key}`}>
                  <FileAudio className="h-2.5 w-2.5 text-sky-400"/>
                  Uses the <b className="text-sky-300 uppercase tracking-wide mx-0.5">{evt.sharesKey}</b> tone — customize it on the Chat Message card.
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* v2.7.45+E17 · Save Audio Configuration — flushes the audit log */}
      <div className="flex items-center justify-between gap-3 bg-zinc-950 border border-zinc-800 rounded p-3" data-testid="audio-test-save-row">
        <div className="text-xs text-zinc-400">
          <FileAudio className="h-3.5 w-3.5 inline mr-1 text-amber-300"/>
          Uploads persist instantly. Click <b className="text-amber-300">Save Audio Configuration</b> to log this checkpoint to the audit ledger. Max <b>{(maxBytes / 1024 / 1024).toFixed(0)} MB</b> per file · accepted formats: <span className="font-mono">.mp3, .wav, .ogg, .m4a, .aac, .flac</span>.
        </div>
        <Button
          onClick={saveAudioConfig}
          disabled={savingConfig}
          className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold gap-1.5 flex-shrink-0"
          data-testid="audio-test-save-config"
        >
          {savingConfig ? <Loader2 className="h-4 w-4 animate-spin"/> : <Save className="h-4 w-4"/>}Save Audio Configuration
        </Button>
      </div>

      {/* Last fired indicator */}
      <Card className="bg-zinc-950 border-zinc-800 p-3" data-testid="audio-test-last-fired">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">Last fired</div>
        {lastFired ? (
          <div className="text-xs text-zinc-200">
            <span className="font-bold text-amber-300">{lastFired.label}</span>
            <span className="text-zinc-500 ml-2 font-mono">{new Date(lastFired.at).toLocaleTimeString()}</span>
          </div>
        ) : (
          <div className="text-xs text-zinc-500 italic">No events fired yet · click "Live" or "Direct" above</div>
        )}
      </Card>
    </Card>
  );
}
